import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { SelectorEvaluationTab } from "@/components/hub/SelectorEvaluationTab";
import { PanelComparisonTab } from "@/components/hub/PanelComparisonTab";
import { SelectionDecisionTab } from "@/components/hub/SelectionDecisionTab";
import {
  MOCK_CANDIDATES,
  MOCK_SCORES,
  type ScoreMap,
  type CriterionKey,
  MOCK_EVALUATORS,
  type EvaluatorName,
} from "@/components/hub/mockSelectionData";

// ---------------------------------------------------------------------------
// DEMO MOCK DATA — not connected to any backend or live system
// ---------------------------------------------------------------------------

export type Bucket = "selected" | "reserves" | "not_selected";

const INITIAL_BUCKETS: Record<Bucket, number[]> = {
  selected:     [1, 2, 5, 11],
  reserves:     [7, 8, 13],
  not_selected: [3, 4, 6, 9, 10, 12, 14],
};

const VALID_AGE_GROUPS = ["U11", "U13", "U15", "U17"] as const;

export default function HubSelectionDemo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const ageGroupParam = (VALID_AGE_GROUPS as readonly string[]).includes(searchParams.get("ageGroup") ?? "")
    ? (searchParams.get("ageGroup") as string)
    : null;

  const filteredCandidates = ageGroupParam
    ? MOCK_CANDIDATES.filter((c) => c.ageGroup === ageGroupParam)
    : MOCK_CANDIDATES;

  const filteredIds = new Set(filteredCandidates.map((c) => c.id));

  const [selectedCandidateId, setSelectedCandidateId] = useState<number>(1);

  // Live scores: evaluator "Selector A" scores can be edited in Tab 1
  const [liveScores, setLiveScores] = useState<ScoreMap>(MOCK_SCORES);

  const effectiveCandidateId = filteredIds.has(selectedCandidateId)
    ? selectedCandidateId
    : (filteredCandidates[0]?.id ?? 1);

  const [buckets, setBuckets] = useState<Record<Bucket, number[]>>(INITIAL_BUCKETS);

  const filteredBuckets: Record<Bucket, number[]> = {
    selected:     buckets.selected.filter((id) => filteredIds.has(id)),
    reserves:     buckets.reserves.filter((id) => filteredIds.has(id)),
    not_selected: buckets.not_selected.filter((id) => filteredIds.has(id)),
  };

  function updateScore(candidateId: number, evaluator: EvaluatorName, key: CriterionKey, value: number) {
    setLiveScores((prev) => ({
      ...prev,
      [candidateId]: {
        ...prev[candidateId],
        [evaluator]: {
          ...prev[candidateId][evaluator],
          [key]: value,
        },
      },
    }));
  }

  function moveCandidateToBucket(candidateId: number, target: Bucket) {
    setBuckets((prev) => {
      const next = { ...prev };
      (Object.keys(next) as Bucket[]).forEach((b) => {
        next[b] = next[b].filter((id) => id !== candidateId);
      });
      next[target] = [...next[target], candidateId];
      return next;
    });
  }

  return (
    <HubAdminDemoLayout>
    <div data-testid="hub-selection-demo-page">
      {/* Page header — selection-specific context only */}
      <header className="bg-[#1E2761] text-white px-4 py-4 md:px-8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Sample Selection Event — Spring Trial</h1>
            {ageGroupParam && (
              <span className="mt-1 inline-block bg-purple-500 text-white text-xs font-semibold px-2 py-0.5 rounded">
                {ageGroupParam} only
              </span>
            )}
          </div>
          <div className="text-right text-sm text-blue-200 hidden sm:block">
            <p>{filteredCandidates.length} candidates</p>
            <p>{MOCK_EVALUATORS.length} selectors</p>
          </div>
        </div>
      </header>

      {/* Age group filter */}
      <div className="max-w-6xl mx-auto px-4 pt-4 md:px-8 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500 mr-1">Age group:</span>
        {(["All", "U11", "U13", "U15", "U17"] as const).map((ag) => {
          const active = ag === "All" ? !ageGroupParam : ageGroupParam === ag;
          return (
            <button
              key={ag}
              onClick={() => {
                if (ag === "All") setSearchParams({});
                else setSearchParams({ ageGroup: ag });
              }}
              className={`px-3 py-1 rounded-full text-sm font-semibold border transition-all ${
                active
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-[#1E2761] hover:text-[#1E2761]"
              }`}
            >
              {ag}
            </button>
          );
        })}
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-6 md:px-8">
        <Tabs defaultValue="evaluation" className="w-full">
          <TabsList className="mb-6 w-full justify-start gap-1 bg-white border border-gray-200 rounded-lg p-1 h-auto flex-wrap">
            <TabsTrigger value="evaluation" data-testid="tab-evaluation">
              Selector Evaluation
            </TabsTrigger>
            <TabsTrigger value="comparison" data-testid="tab-comparison">
              Panel Comparison
            </TabsTrigger>
            <TabsTrigger value="decision" data-testid="tab-decision">
              Selection Decision
            </TabsTrigger>
          </TabsList>

          <TabsContent value="evaluation">
            <SelectorEvaluationTab
              candidates={filteredCandidates}
              selectedCandidateId={effectiveCandidateId}
              onSelectCandidate={setSelectedCandidateId}
              scores={liveScores}
              onUpdateScore={updateScore}
            />
          </TabsContent>

          <TabsContent value="comparison">
            <PanelComparisonTab scores={liveScores} candidates={filteredCandidates} />
          </TabsContent>

          <TabsContent value="decision">
            <SelectionDecisionTab
              candidates={filteredCandidates}
              scores={liveScores}
              buckets={filteredBuckets}
              onMove={moveCandidateToBucket}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </HubAdminDemoLayout>
  );
}
